/**
 * {@link DataTable} for subjects.
 */
import * as React from 'react';
import { BookOpen } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { SubjectRow } from './SubjectRow';
import { SubjectCard } from './SubjectCard';
import { SubjectFiltersContent } from './SubjectFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderSubjectDetails } from './renderSubjectDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getSubjects, deleteSubject, exportSubjectsExcel } from '@/services/subjects.service';
import { cn } from '@/lib/utils';
import {
  DEFAULT_SUBJECT_FILTERS,
  ALL_FILTER_GROUP_IDS,
  type SubjectFilters,
  type FilterGroupId,
  FILTER_GROUP_LABELS,
} from './subject-configs';
import { hasSubjectActiveFilters, toSubjectListParams } from './subject-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { Subject } from '@/types/subject.types';

/** Props for {@link SubjectsDataTable}. */
interface SubjectsDataTableProps {
  showCreateButton?: boolean;
  onCreateSubject?: () => void;
  subjects?: Subject[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditSubject?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: SubjectFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
}

/** Subjects list / grid with filters, details, delete, and Excel export. */
export const SubjectsDataTable: React.FC<SubjectsDataTableProps> = ({
  subjects: externalSubjects,
  showCreateButton = false,
  onCreateSubject,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditSubject,
  enableFilters = true,
  defaultFilters = DEFAULT_SUBJECT_FILTERS,
  layoutWidth = 'full',
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const dt = useDataTable<SubjectFilters, FilterGroupId>({
    tableName: 'subjects',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['subjects', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () =>
      getSubjects(toSubjectListParams(dt.filters, dt.currentPage, dt.itemsPerPage)),
    enabled: !externalSubjects,
  });

  const data = React.useMemo(
    () => externalSubjects ?? (apiResponse?.data || []),
    [externalSubjects, apiResponse?.data],
  );
  const totalItems = apiResponse?.total ?? data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalSubjects ? data.length === 0 : !isLoading && totalItems === 0),
    [externalSubjects, data.length, isLoading, totalItems],
  );

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportSubjectsExcel();
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport]);

  const {
    data: expandedSubjectData,
    isLoading: isLoadingExpandedSubject,
    isError: isExpandedSubjectError,
    error: expandedSubjectError,
  } = useQuery({
    queryKey: ['subjects', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getSubject } = await import('@/services/subjects.service');
      return await getSubject(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedSubjectError,
    error: expandedSubjectError,
    catalogKey: 'subjectNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => hasSubjectActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: deleteSubject,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subjects'] });
      dt.setDeleteId(null);
      toast.success('Matière supprimée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const subjectToDelete = React.useMemo(
    () => data.find((s) => s.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const toggleCycleFilter = React.useCallback(
    (cycle: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        cycles: prev.cycles.includes(cycle)
          ? prev.cycles.filter((c) => c !== cycle)
          : [...prev.cycles, cycle],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleCoreFilter = React.useCallback(() => {
    dt.setFilters((prev) => {
      let nextValue: boolean | null;
      if (prev.isCore === null) nextValue = true;
      else if (prev.isCore === true) nextValue = false;
      else nextValue = null;
      return { ...prev, isCore: nextValue };
    });
    dt.setCurrentPage(1);
  }, [dt]);

  const filtersContent = React.useMemo(
    () => (
      <SubjectFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleCycle={toggleCycleFilter}
        onToggleStatus={toggleStatusFilter}
        onToggleCore={toggleCoreFilter}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleCycleFilter,
      toggleStatusFilter,
      toggleCoreFilter,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedSubject = expandedSubjectData ?? null;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="matières"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                dt.setShowFilters((prev) => {
                  if (prev) dt.setShowConfigure(false);
                  return !prev;
                });
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des matières en Excel'
              }
            />
          )}
          {showCreateButton && onCreateSubject && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer une matière"
              onClick={() => {
                onCloseDetails?.();
                onCreateSubject();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="matière(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Code</th>
          <th>Libellé</th>
          <th>Cycle éducatif</th>
          <th>Type</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(subject) => (
        <SubjectRow
          subject={subject}
          onView={onToggleRow ? () => onToggleRow(subject.id) : undefined}
          onEdit={onEditSubject}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(subject) => (
        <DetailsViewContent>{renderSubjectDetails(subject)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(subject) => (
        <SubjectCard
          subject={subject}
          onView={onToggleRow ? () => onToggleRow(subject.id) : undefined}
          onEdit={onEditSubject}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={BookOpen}
      emptyText="Aucune matière trouvée"
      title="Matières"
      sidebarMenuId="menu-subjects"
      isSection={isSection}
      expandedItem={expandedSubject}
      isExpandedDetailLoading={isLoadingExpandedSubject}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(subject) => (
        <DetailsViewContent>{renderSubjectDetails(subject)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={subjectToDelete?.label}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
